/* eslint-disable arrow-body-style */

import getStore from '@ve/services/getStore/getStore';
import processProducts from '@ve/components/hotel/filters/common/store/processProducts';
import moment from 'moment';
import { TGetters } from '@ve/components/hotel/emptySearch/common/store/types';

export const NAMESPACE = 'hotelPageEmptySearchStore';

type IStoreHotelPageOptions = {
    tourSearchModel: any;
}

export default function HotelPageEmptySearchStore({
    tourSearchModel,
}: IStoreHotelPageOptions) {
    return getStore({
        name: NAMESPACE,
        backboneMap: [{
            fields: [{
                syncTourProductsOnSelectedDateAdapted: {
                    field: 'syncTourProductsOnSelectedDate',
                    action() {
                        const products = tourSearchModel.get('syncTourProductsOnSelectedDate');
                        return processProducts(products, tourSearchModel);
                    },
                },
            }],
            model: tourSearchModel,
        }],
        storeOptions: {
            getters: {
                isSearchDepartureCityFromRegion: (_state, _getters, { mapForSearchData: { searchDepartureCity } }) => {
                    return searchDepartureCity.id > 2;
                },

                isFilteredProductsAreEmptyOnSelectedDate: (_state, _getters, { mapForFilters: { filteredTourProductsByDates } }) => {
                    return filteredTourProductsByDates.length === 0;
                },
                isFilteredProductsAreExistsOnOtherDates: (_state, { otherDatesInterval }, { mapForFilters: { filteredTourProducts } }) => {
                    return filteredTourProducts.some((product) => otherDatesInterval.includes(product.startDate));
                },
                isProductsOfSelectedAndOtherDatesAreEmpty: (_state, { isFilteredProductsAreEmptyOnSelectedDate, isFilteredProductsAreExistsOnOtherDates }) => {
                    return isFilteredProductsAreEmptyOnSelectedDate && !isFilteredProductsAreExistsOnOtherDates;
                },

                isProductsOfSelectedDateAreEmptyButExistsOnOtherDates: (_state, { isFilteredProductsAreEmptyOnSelectedDate, isFilteredProductsAreExistsOnOtherDates }) => {
                    return isFilteredProductsAreEmptyOnSelectedDate && isFilteredProductsAreExistsOnOtherDates;
                },
                isSyncProductsExistsOnSelectedDate: ({ syncTourProductsOnSelectedDateAdapted }) => syncTourProductsOnSelectedDateAdapted.length > 0,
                isSyncProductsOnSelectedDateIncludesTourProducts: ({ syncTourProductsOnSelectedDateAdapted }) => {
                    return syncTourProductsOnSelectedDateAdapted.filter((product) => product.type === 'TourProduct').length > 0;
                },

                selectedDateText: (_state, _getters, _rootState, rootGetters) => {
                    const selectedDate = rootGetters['mapForDatesTabs/selectedDate'];
                    return moment(selectedDate, 'YYYY-MM-DD').format('D MMMM');
                },

                otherDatesInterval: (_state, _getters, _rootState, rootGetters) => {
                    const dateInterval = 3;
                    const selectedDate = rootGetters['mapForDatesTabs/selectedDate'];
                    const otherDatesInterval = [];
                    let currentDate = moment(new Date(selectedDate)).subtract(dateInterval, 'days');
                    const endDate = moment(new Date(selectedDate)).add(dateInterval, 'days');
                    while (currentDate <= endDate) {
                        const currentDateFormatted = moment(currentDate).format('YYYY-MM-DD');
                        if (currentDateFormatted !== selectedDate) otherDatesInterval.push(currentDateFormatted);
                        currentDate = moment(currentDate).add(1, 'days');
                    }
                    return otherDatesInterval;
                },

                otherDatesAvailableInInterval: (_state, { otherDatesInterval }, { mapForFilters: { filteredTourProducts } }) => {
                    const allDates: Array<string> = Array.from((filteredTourProducts).reduce((currentDates: Set<string>, product): Set<string> => {
                        if (otherDatesInterval.includes(product.startDate)) currentDates.add(product.startDate);
                        return currentDates;
                    }, new Set('')));
                    const datesSorted = allDates?.sort();
                    return datesSorted.map((date) => moment(date, 'YYYY-MM-DD').format('D MMMM'));
                },

                isLoadingStateFinished: (_state, _getters, { mapForFilters: { loadingState } }) => {
                    return loadingState === 'finished';
                },

                isShouldRenderEmptySearchBlock: (_state, { isLoadingStateFinished, isProductsOfSelectedAndOtherDatesAreEmpty, isProductsOfSelectedDateAreEmptyButExistsOnOtherDates }) => {
                    return isLoadingStateFinished && (isProductsOfSelectedAndOtherDatesAreEmpty || isProductsOfSelectedDateAreEmptyButExistsOnOtherDates);
                },
                isShouldRenderAnotherProductsBlock: ({ syncTourProductsOnSelectedDateAdapted }, { isLoadingStateFinished }, { mapForFilters: { filteredTourProductsByDates } }) => {
                    return isLoadingStateFinished && syncTourProductsOnSelectedDateAdapted.length > 0 && filteredTourProductsByDates.length === 0;
                },

                paramsForEmptySearchAnalytic: (_state, {
                    isShouldRenderEmptySearchBlock,
                    isSyncProductsOnSelectedDateIncludesTourProducts,
                    isFilteredProductsAreExistsOnOtherDates,
                    isShouldRenderAnotherProductsBlock,
                }) => ({
                    isEmptySearch: isShouldRenderEmptySearchBlock,
                    isTourProductsOnSelectedDate: isSyncProductsOnSelectedDateIncludesTourProducts,
                    isProductsOnOtherDates: isFilteredProductsAreExistsOnOtherDates,
                    isAlternativeProductsOnSelectedDate: isShouldRenderAnotherProductsBlock,
                }),
            } as TGetters,
        },
    });
}